import { useState } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Users,
  Building2,
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  Wallet,
  Ambulance,
  ClipboardCheck,
  Link2,
  Check,
  Banknote,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  BookOpen,
} from 'lucide-react'

import LanguageSwitcher from './LanguageSwitcher'
import { apiClient, ApiError } from '../services/api'
import { useOrganization } from '../contexts/OrganizationContext'
import { usePermissions } from '../hooks/usePermissions'

declare const __APP_VERSION__: string
const RAW_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'
// Staging: short commit SHA (7 chars), Production: git tag (v1.2.0), Local: package.json
const APP_VERSION = RAW_VERSION.length > 12 ? RAW_VERSION.slice(0, 7) : RAW_VERSION

const APP_ENV = import.meta.env.VITE_APP_ENV as string | undefined

interface UserInfo {
  role: string
  is_system_admin_org: boolean
  organization_name: string
  can_review_invoices?: boolean
  can_view_reports?: boolean
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
  organization_settings?: {
    features?: {
      ambulance_orders_enabled?: boolean
    }
  }
}

interface Organization {
  id: string
  name: string
}

interface OrganizationsResponse {
  data: Organization[]
}

function getSectionForPath(pathname: string): string {
  if (pathname.startsWith('/invoices')) return 'invoices'
  if (pathname.startsWith('/reports')) return 'reports'
  if (pathname.startsWith('/ambulance-orders')) return 'ambulance'
  if (pathname.startsWith('/cost-centers')) return 'costCenters'
  if (pathname.startsWith('/users')) return 'users'
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname.startsWith('/organizations')) return 'organizations'
  if (pathname.startsWith('/payment-traffic')) return 'paymentTraffic'
  if (pathname.startsWith('/security-groups')) return 'securityGroups'
  if (pathname.startsWith('/audit')) return 'navigation'
  if (pathname.startsWith('/dashboard')) return 'dashboard'
  return ''
}

export default function Layout() {
  const { t } = useTranslation()
  const { instance, accounts } = useMsal()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true'
  })
  const [linkCopied, setLinkCopied] = useState(false)
  const { selectedOrgId, setSelectedOrgId, getShareableUrl } = useOrganization()

  const account = accounts[0]
  const displayName = account?.name || account?.username || ''

  // Fetch user info for role-based menu visibility
  const { data: userInfo, error: userError, isLoading: userLoading } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 403 (access denied) — user is not provisioned
      if (error instanceof ApiError && error.status === 403) return false
      return failureCount < 2
    },
  })

  // Permissions from shared hook (same query cache as /auth/me).
  // Ambulance access already follows the selected org inside the hook,
  // so the sidebar and the route guards (RequirePermission) stay in sync.
  const {
    isAdmin,
    isSystemAdmin,
    canAccessInvoices,
    canAccessReports,
    canAccessAmbulance,
    canReviewAmbulance,
  } = usePermissions()

  // Fetch organizations list for sys admin dropdown
  const { data: orgsData } = useQuery<OrganizationsResponse>({
    queryKey: ['organizations'],
    queryFn: () => apiClient.get('/organizations'),
    enabled: isSystemAdmin,
  })

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('sidebar-collapsed', String(next))
      return next
    })
  }

  const handleLogout = async () => {
    await instance.logoutRedirect()
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareableUrl())
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  // Get selected organization name for display
  const selectedOrgName = selectedOrgId 
    ? orgsData?.data.find(o => o.id === selectedOrgId)?.name 
    : userInfo?.organization_name

  const navigation = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    ...(canAccessInvoices ? [{ name: t('navigation.invoices'), href: '/invoices', icon: FileText }] : []),
    ...(canAccessReports ? [{ name: t('navigation.reports'), href: '/reports', icon: BarChart3 }] : []),
    ...(canAccessAmbulance ? [{ name: t('navigation.ambulanceOrders'), href: '/ambulance-orders', icon: Ambulance }] : []),
    ...(canReviewAmbulance ? [{ name: t('navigation.ambulanceReview'), href: '/ambulance-orders/review', icon: ClipboardCheck }] : []),
    ...(isAdmin ? [{ name: t('navigation.users'), href: '/users', icon: Users }] : []),
    ...(isAdmin ? [{ name: t('navigation.costCenters'), href: '/cost-centers', icon: Wallet }] : []),
    ...(isSystemAdmin ? [{ name: t('navigation.paymentTraffic'), href: '/payment-traffic', icon: Banknote }] : []),
    ...(isSystemAdmin ? [{ name: t('navigation.organizations'), href: '/organizations', icon: Building2 }] : []),
    ...(isSystemAdmin ? [{ name: t('navigation.securityGroups'), href: '/security-groups', icon: Shield }] : []),
    ...(isAdmin ? [{ name: t('navigation.audit'), href: '/audit', icon: ClipboardList }] : []),
    { name: t('navigation.settings'), href: '/settings', icon: Settings },
    { name: t('navigation.help'), href: '/help', icon: BookOpen },
  ]

  // Show error page if user is not authorized (403 from /auth/me)
  if (userError && userError instanceof ApiError && userError.status === 403) {
    const errorCode = userError.code || 'UNKNOWN_ERROR'
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('errors.forbidden')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t(`errors.${errorCode}`, { defaultValue: t('errors.generic') })}
          </p>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('navigation.logout')}
          </button>
        </div>
      </div>
    )
  }

  // Show loading while checking user authorization
  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Full-width black top header */}
      <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-black flex items-center">
        {/* Logo area - fixed width, not affected by sidebar collapse */}
        <div className="hidden lg:flex items-center flex-shrink-0 w-64 px-4">
          <img src="/images/taksihelsinki-logo-header-desktop.svg" alt="Taksi Helsinki" className="h-8" />
        </div>
        {/* Title and user info */}
        <div className="flex-1 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <span className="text-lg font-semibold text-white">Tilaajakanava</span>
            {APP_ENV === 'staging' && (
              <span className="px-2 py-0.5 text-xs font-bold rounded bg-orange-400 text-orange-900 uppercase tracking-wide">
                STAGING
              </span>
            )}
            {APP_ENV === 'development' && (
              <span className="px-2 py-0.5 text-xs font-bold rounded bg-yellow-400 text-yellow-900 uppercase tracking-wide">
                DEV
              </span>
            )}
            {isSystemAdmin && orgsData?.data ? (
              <div className="flex items-center gap-2">
                <select
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="px-3 py-1.5 bg-gray-800 text-white border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{userInfo?.organization_name} ({t('common.myOrganization')})</option>
                  {orgsData.data
                    .filter(org => org.name !== userInfo?.organization_name)
                    .map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                </select>
                {selectedOrgId && (
                  <button
                    onClick={handleCopyLink}
                    className="p-1.5 text-gray-400 hover:text-white transition-colors rounded"
                    title={linkCopied ? t('common.linkCopied') : t('common.copyLink')}
                  >
                    {linkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Link2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            ) : (
              selectedOrgName && (
                <span className="text-gray-400">– {selectedOrgName}</span>
              )
            )}
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => {
                const section = getSectionForPath(location.pathname)
                navigate(section ? `/help#${section}` : '/help')
              }}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
              title={t('navigation.help')}
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden lg:inline">{t('navigation.help')}</span>
            </button>
            <div className="text-sm text-gray-300">
              {displayName}
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              {t('navigation.logout')}
            </button>
          </div>
        </div>
        {/* Mobile menu button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden absolute right-4 text-white"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b bg-black">
          <img src="/images/taksihelsinki-logo-header-desktop.svg" alt="Taksi Helsinki" className="h-8" />
          <button onClick={() => setSidebarOpen(false)} className="text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-400 text-gray-900 font-medium'
                    : 'text-gray-700 hover:bg-primary-300 hover:text-gray-900'
                }`
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:top-16 lg:bottom-0 lg:left-0 lg:flex lg:flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow bg-white border-r overflow-hidden">
          <nav className={`flex-1 mt-4 ${sidebarCollapsed ? 'px-1' : 'px-3'}`}>
            {navigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                title={sidebarCollapsed ? item.name : undefined}
                className={({ isActive }) =>
                  `flex items-center mb-1 rounded-lg transition-colors ${
                    sidebarCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'
                  } ${
                    isActive
                      ? 'bg-primary-200 text-gray-900 font-medium'
                      : 'text-gray-700 hover:bg-primary-100 hover:text-gray-900'
                  }`
                }
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
              </NavLink>
            ))}
          </nav>
          <div className={`border-t ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            <button
              onClick={toggleSidebar}
              className={`flex items-center w-full mt-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors ${
                sidebarCollapsed ? 'justify-center px-2 py-2' : 'px-4 py-2'
              }`}
              title={sidebarCollapsed ? t('navigation.expandSidebar') : t('navigation.collapseSidebar')}
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="w-5 h-5 flex-shrink-0" />
              ) : (
                <>
                  <PanelLeftClose className="w-5 h-5 flex-shrink-0 mr-3" />
                  <span className="text-sm">{t('navigation.collapseSidebar')}</span>
                </>
              )}
            </button>
            {!sidebarCollapsed && <p className="text-xs text-gray-400 text-center mt-2">{APP_VERSION.startsWith('v') ? APP_VERSION : `v${APP_VERSION}`}</p>}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="px-4 py-6 text-center text-sm text-gray-500 lg:px-8">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </footer>
      </div>
    </div>
  )
}
